/**
 * chartState.js — Canonical chart period-change state contract
 *
 * Shared by both chart engines (LightweightChartsEngine, TradingViewLWChart)
 * and all consuming pages/components.
 *
 * State flow:
 *   Parent resets periodChangeData to null on timeframe switch
 *   → Chart engine starts fetching → fires onPeriodChange(periodUnavailable/periodReady/periodIntegrityFail)
 *   → Parent updates periodChangeData → displayQuote reacts
 *
 * For non-1D timeframes, null periodChangeData means "loading" (chart hasn't responded yet).
 */

// ── State constants ──────────────────────────────────────────────
export const PERIOD_STATES = Object.freeze({
  LOADING:        'loading',
  READY:          'ready',
  UNAVAILABLE:    'unavailable',
  INTEGRITY_FAIL: 'integrity_fail',
});

// ── Factory functions — used by chart engines ────────────────────

/** Chart data loaded successfully — period return is computed and trusted. */
export function periodReady(firstPrice, lastPrice) {
  const periodChange = lastPrice - firstPrice;
  const periodChangePercent = firstPrice > 0 ? (periodChange / firstPrice) * 100 : 0;
  return { state: PERIOD_STATES.READY, periodChange, periodChangePercent, firstPrice, lastPrice };
}

/** Chart data failed to load, is empty, or timed out. */
export function periodUnavailable(reason) {
  return { state: PERIOD_STATES.UNAVAILABLE, reason: reason || 'no_data' };
}

/** Chart integrity guard detected corrupted/wrong-instrument data. */
export function periodIntegrityFail(firstPrice, lastPrice, guardVersion) {
  return { state: PERIOD_STATES.INTEGRITY_FAIL, firstPrice, lastPrice, guardVersion };
}

// ── Query helpers — used by consuming pages ──────────────────────

/** True when chart data has loaded with valid period return data. */
export function isPeriodReady(pd) {
  return pd != null && pd.state === PERIOD_STATES.READY;
}

/** True when chart data is unavailable or integrity-failed. */
export function isPeriodUnavailable(pd) {
  return pd != null && (pd.state === PERIOD_STATES.UNAVAILABLE || pd.state === PERIOD_STATES.INTEGRITY_FAIL);
}

/** True when we're still waiting for chart data (null or explicit loading). */
export function isPeriodLoading(pd) {
  return pd == null || pd.state === PERIOD_STATES.LOADING;
}
